/**
 * TOSS Web Application - Messenger TanStack Query Hooks
 *
 * React hooks for messenger using TanStack Query:
 * - useChats - Get paginated chats list
 * - useChat - Get single chat details
 * - useMessages - Get messages with infinite scroll (cursor-based)
 * - useSendMessage - Send a message mutation
 * - useEditMessage - Edit a message mutation
 * - useDeleteMessage - Delete a message mutation
 * - useCreateChat - Create chat mutation
 * - useMarkAsRead - Mark message as read
 * - usePostApplicationCard - Post an application card into a chat (Р27)
 * - useSearchMessages - Search messages
 * - useMessengerUnreadCount - Get total unread count for sidebar badge
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { queryKeys } from '@/app/queryClient';
import { isNotFoundError } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { useMessengerStore } from '@/store/messengerStore';
import { messengerApi } from '@/api/messenger.api';
import {
  MessageType,
  ChatMemberRole,
  type ChatMember,
  type ChatShort,
  type ChatFull,
  type ChatMessage,
  type ChatListResponse,
  type MessageListResponse,
  type CreateChatRequest,
  type CreateCustomerChatRequest,
  type UpdateChatRequest,
  type SendMessageRequest,
  type EditMessageRequest,
  type ForwardMessagesRequest,
  type ChatListFilter,
  type UnreadCountResponse,
  type SearchResultResponse,
} from '@/types/messenger.types';
import type { QueryClient } from '@tanstack/react-query';
import type { ChatDeleteScope } from '@/utils/chatDeleteDialog';
import {
  messagePreviewText,
  stripMentionTagsForPreview,
  updateMessageInCache,
} from './messenger/cache';

// Non-hook cache mutators live in ./messenger/cache and are re-exported here so
// consumers keep importing them from '@/hooks/api/useMessenger'.
export {
  addMessageToCache,
  removeMessageFromCache,
  invalidateMessengerChats,
  updatePinnedMessageInCache,
  isChatInCachedLists,
  updateMessagesByFileIdInCache,
} from './messenger/cache';
export { updateMessageInCache };

// ============================================================================
// Chats List Hook
// ============================================================================

export function useChats(filter?: ChatListFilter | null) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<ChatListResponse, Error>({
    queryKey: queryKeys.messenger.chatList(filter as Record<string, unknown>),
    queryFn: () => messengerApi.getChats(filter ?? undefined),
    enabled: isAuthenticated && filter !== null,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30000,
  });
}

// ============================================================================
// Single Chat Hook
// ============================================================================

/**
 * Карточка одной переписки.
 *
 * `placeholderData: undefined` гасит глобальный keep-previous-data
 * (`app/queryClient.ts`): у запроса, ключ которого — идентификатор чата, «предыдущие данные» это
 * не прошлое состояние этого чата, а ДРУГОЙ чат. С ним переход в свежесозданную переписку рисовал
 * шапку нового чата над составом старого, пока идёт запрос. То же решение и по той же причине, что
 * в `useStation` и `useServiceFull`.
 */
export function useChat(chatId: string | null) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<ChatFull, Error>({
    queryKey: queryKeys.messenger.chat(chatId ?? ''),
    queryFn: () => messengerApi.getChatById(chatId!),
    enabled: isAuthenticated && !!chatId,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: undefined,
  });
}

// ============================================================================
// Messages Infinite Query Hook
// ============================================================================

export function useMessages(chatId: string | null) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isDisconnected = useMessengerStore((state) => state.connectionState !== 'Connected');

  return useInfiniteQuery<MessageListResponse, Error>({
    queryKey: queryKeys.messenger.messages(chatId ?? ''),
    queryFn: ({ pageParam }) =>
      messengerApi.getMessages(chatId!, {
        cursor: pageParam as string | undefined,
        pageSize: 30,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled: isAuthenticated && !!chatId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // Poll every 15s when SignalR is disconnected (only first page refetches)
    refetchInterval: isDisconnected ? 15_000 : false,
    // Чужая переписка вместо скелета — см. {@link useChat}. Здесь цена ошибки выше: под новым
    // заголовком показывались сообщения предыдущего чата.
    placeholderData: undefined,
  });
}

// ============================================================================
// Send Message Mutation
// ============================================================================

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation<
    ChatMessage,
    Error,
    { chatId: string; data: SendMessageRequest; previewUrl?: string },
    { tempId: string }
  >({
    mutationFn: ({ chatId, data }) => messengerApi.sendMessage(chatId, data),

    onMutate: async ({ chatId, data, previewUrl }) => {
      const tempId = `temp_${crypto.randomUUID()}`;
      const user = useAuthStore.getState().user;
      const replyTo = useMessengerStore.getState().replyTo;

      const optimisticMessage: ChatMessage = {
        id: tempId,
        chatId,
        senderId: user?.id ?? '',
        senderName: user?.fullName ?? '',
        senderPhotoUrl: user?.photoUrl,
        content: data.content,
        messageType: data.messageType ?? MessageType.Text,
        replyTo: replyTo && data.replyToMessageId
          ? { id: replyTo.id, senderName: replyTo.senderName, content: replyTo.content }
          : undefined,
        // Ссылка на уже загруженный файл, если отправитель её передал: вложение уезжает на сервер
        // при выборе, задолго до самого сообщения, поэтому картинку видно сразу.
        fileUrl: previewUrl,
        fileName: data.fileName,
        fileContentType: data.fileContentType,
        fileSize: data.fileSize,
        attachmentGroupId: data.attachmentGroupId,
        isEdited: false,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        _status: 'sending',
        _fileId: data.fileId,
      };

      // Add optimistic message to cache immediately
      queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
        queryKeys.messenger.messages(chatId),
        (oldData) => {
          if (!oldData) return oldData;
          const firstPage = oldData.pages[0];
          return {
            ...oldData,
            pages: [
              { ...firstPage, items: [optimisticMessage, ...(firstPage?.items ?? [])] },
              ...oldData.pages.slice(1),
            ],
          };
        }
      );

      // Update chat list lastMessage optimistically
      queryClient.setQueriesData<ChatListResponse>(
        { queryKey: queryKeys.messenger.chats() },
        (oldData) => {
          if (!oldData?.items) return oldData;
          return {
            ...oldData,
            items: oldData.items.map((chat) =>
              chat.id === chatId
                ? {
                    ...chat,
                    lastMessage: stripMentionTagsForPreview(messagePreviewText(data), chatId, queryClient),
                    lastMessageAt: optimisticMessage.createdAt,
                  }
                : chat
            ),
          };
        }
      );

      return { tempId };
    },

    onSuccess: (realMessage, { chatId }, context) => {
      if (!context) return;
      // Replace temp message with real one (handles SignalR race condition too)
      replaceTempWithReal(queryClient, chatId, context.tempId, realMessage);
      // Previously also invalidated chats(), which caused a full refetch on
      // every successful send. The chat list was already updated optimistically
      // in onMutate (lastMessage, lastMessageAt), and the backend broadcasts
      // ChatUnreadIncremented / UnreadCountChanged via SignalR to keep other
      // sessions in sync — no extra roundtrip needed here.
    },

    onError: (_error, { chatId }, context) => {
      if (!context?.tempId) return;
      // Mark temp message as failed so user can retry
      queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
        queryKeys.messenger.messages(chatId),
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: page.items.map((msg) =>
                msg.id === context.tempId ? { ...msg, _status: 'failed' as const } : msg
              ),
            })),
          };
        }
      );
    },
  });
}

/**
 * Replace a temp optimistic message with the real server message.
 * Handles race condition where SignalR may have already delivered the real message.
 */
function replaceTempWithReal(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string,
  tempId: string,
  realMessage: ChatMessage,
): void {
  queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
    queryKeys.messenger.messages(chatId),
    (oldData) => {
      if (!oldData) return oldData;
      // Remove both temp and any existing real (dedup from SignalR race)
      const cleanPages = oldData.pages.map((page) => ({
        ...page,
        items: page.items.filter((msg) => msg.id !== tempId && msg.id !== realMessage.id),
      }));
      // Add real message at the top of first page
      return {
        ...oldData,
        pages: [
          { ...cleanPages[0], items: [realMessage, ...(cleanPages[0]?.items ?? [])] },
          ...cleanPages.slice(1),
        ],
      };
    }
  );
}

// ============================================================================
// Edit Message Mutation
// ============================================================================

export function useEditMessage() {
  const queryClient = useQueryClient();

  return useMutation<
    ChatMessage,
    Error,
    { chatId: string; messageId: string; data: EditMessageRequest }
  >({
    mutationFn: ({ chatId, messageId, data }) =>
      messengerApi.editMessage(chatId, messageId, data),
    onSuccess: (updatedMessage, { chatId, messageId }) => {
      queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
        queryKeys.messenger.messages(chatId),
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: (page.items ?? []).map((msg) =>
                msg.id === messageId ? updatedMessage : msg
              ),
            })),
          };
        }
      );
    },
  });
}

// ============================================================================
// Delete Message Mutation
// ============================================================================

export function useDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { chatId: string; messageId: string }>({
    mutationFn: ({ chatId, messageId }) =>
      messengerApi.deleteMessage(chatId, messageId),
    onSuccess: (_, { chatId, messageId }) => {
      queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
        queryKeys.messenger.messages(chatId),
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: (page.items ?? []).map((msg) =>
                msg.id === messageId ? { ...msg, isDeleted: true, content: undefined } : msg
              ),
            })),
          };
        }
      );
      // Refetch chat list to get updated LastMessagePreview from backend
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}

// ============================================================================
// Chat Cache Helpers (module-local)
// ============================================================================

/** Invalidate the chat list plus a single chat's detail query. */
function invalidateChatAndList(qc: QueryClient, chatId: string): void {
  qc.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
  qc.invalidateQueries({ queryKey: queryKeys.messenger.chat(chatId) });
}

/** Clear the active chat when it is the one being removed, then drop its cached data. */
function closeAndPurgeChat(qc: QueryClient, chatId: string): void {
  const { activeChatId, setActiveChatId } = useMessengerStore.getState();
  if (activeChatId === chatId) {
    setActiveChatId(null);
  }
  qc.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
  qc.removeQueries({ queryKey: queryKeys.messenger.chat(chatId) });
  qc.removeQueries({ queryKey: queryKeys.messenger.messages(chatId) });
}

// ============================================================================
// Update Chat Mutation
// ============================================================================

export function useUpdateChat() {
  const queryClient = useQueryClient();

  return useMutation<ChatFull, Error, { chatId: string; data: UpdateChatRequest }>({
    mutationFn: ({ chatId, data }) => messengerApi.updateChat(chatId, data),
    onSuccess: (updatedChat) => {
      invalidateChatAndList(queryClient, updatedChat.id);
    },
  });
}

// ============================================================================
// Upload Chat Avatar Mutation
// ============================================================================

export function useUploadChatAvatar() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; avatarUrl: string; message: string }, Error, { chatId: string; file: File }>({
    mutationFn: ({ chatId, file }) => messengerApi.uploadChatAvatar(chatId, file),
    onSuccess: (_, { chatId }) => {
      invalidateChatAndList(queryClient, chatId);
    },
  });
}

// ============================================================================
// Upload Chat Background Mutation
// ============================================================================

export function useUploadChatBackground() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; avatarUrl: string; message: string }, Error, { chatId: string; file: File }>({
    mutationFn: ({ chatId, file }) => messengerApi.uploadChatBackground(chatId, file),
    onSuccess: (_, { chatId }) => {
      invalidateChatAndList(queryClient, chatId);
    },
  });
}

// ============================================================================
// Delete Chat Background Mutation
// ============================================================================

export function useDeleteChatBackground() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (chatId) => messengerApi.deleteChatBackground(chatId),
    onSuccess: (_, chatId) => {
      invalidateChatAndList(queryClient, chatId);
    },
  });
}

// ============================================================================
// Add Member Mutation
// ============================================================================

export function useAddMember() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { chatId: string; userId: string; grantFullHistory?: boolean }>({
    mutationFn: ({ chatId, userId, grantFullHistory }) =>
      messengerApi.addMember(chatId, userId, grantFullHistory),
    onSuccess: (_, { chatId }) => {
      invalidateChatAndList(queryClient, chatId);
    },
  });
}

// ============================================================================
// Remove Member Mutation
// ============================================================================

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { chatId: string; userId: string }>({
    mutationFn: ({ chatId, userId }) => messengerApi.removeMember(chatId, userId),
    onSuccess: (_, { chatId }) => {
      invalidateChatAndList(queryClient, chatId);
    },
  });
}

// ============================================================================
// Update Member Role Mutation
// ============================================================================

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation<ChatMember, Error, { chatId: string; userId: string; role: ChatMemberRole }>({
    mutationFn: ({ chatId, userId, role }) => messengerApi.updateMemberRole(chatId, userId, role),
    onSuccess: (_, { chatId }) => {
      invalidateChatAndList(queryClient, chatId);
    },
  });
}

// ============================================================================
// Create Chat Mutation
// ============================================================================

export function useCreateChat() {
  const queryClient = useQueryClient();

  return useMutation<ChatFull, Error, CreateChatRequest>({
    mutationFn: messengerApi.createChat,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}

// ============================================================================
// Customer Chats (Shared)
// ============================================================================

/**
 * Заводит дополнительный чат с компанией-заказчиком.
 *
 * Инвалидирует весь префикс `chats()`, а не строку конкретной папки: ключ списка включает объект
 * фильтра (`chatList(filter)`), так что адресовать папку «Клиенты» точечно нечем — ровно как в
 * {@link useCreateChat}.
 */
export function useCreateCustomerChat() {
  const queryClient = useQueryClient();

  return useMutation<ChatFull, Error, CreateCustomerChatRequest>({
    mutationFn: messengerApi.createCustomerChat,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}

/** Удаляет дополнительный чат с заказчиком (сервер разрешит, только пока в нём нет переписки). */
export function useDeleteCustomerChat() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (chatId) => messengerApi.deleteCustomerChat(chatId),
    onSuccess: (_, chatId) => {
      closeAndPurgeChat(queryClient, chatId);
    },
  });
}

/**
 * Портальные учётные записи компании как кандидаты в участники.
 *
 * `customerId === null` — компания ещё не выбрана, и запрос не уходит: модалка открывается шагом
 * выбора компании, а не пустым списком людей.
 */
export function useCustomerChatCandidates(customerId: string | null) {
  return useQuery({
    queryKey: queryKeys.messenger.customerChatCandidates(customerId ?? ''),
    queryFn: () => messengerApi.getCustomerChatCandidates(customerId!),
    enabled: !!customerId,
    staleTime: 60_000,
  });
}

// ============================================================================
// Delete Chat Mutation
// ============================================================================

export function useDeleteChat() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { chatId: string; scope: ChatDeleteScope }>({
    mutationFn: ({ chatId, scope }) => messengerApi.deleteChat(chatId, scope),
    onSuccess: (_, { chatId }) => {
      closeAndPurgeChat(queryClient, chatId);
    },
    onError: (err, { chatId }) => {
      // Somebody else's delete (or ours, from another tab) already won the race — the chat is
      // gone either way, so the cache should agree with the server regardless of which side the
      // caller sees as "failed". The component still treats this as success (no error toast).
      if (isNotFoundError(err)) {
        closeAndPurgeChat(queryClient, chatId);
      }
    },
  });
}

// ============================================================================
// Leave Chat Mutation
// ============================================================================

export function useLeaveChat() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (chatId) => messengerApi.leaveChat(chatId),
    onSuccess: (_, chatId) => {
      closeAndPurgeChat(queryClient, chatId);
    },
  });
}

// ============================================================================
// Pin / Unpin Chat Mutations
// ============================================================================

export function usePinChat() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (chatId) => messengerApi.pinChat(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}

export function useUnpinChat() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (chatId) => messengerApi.unpinChat(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}

// ============================================================================
// Mark As Read Mutation
// ============================================================================

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { chatId: string; messageId: string }>({
    mutationFn: ({ chatId, messageId }) =>
      messengerApi.markAsRead(chatId, messageId),
    onSuccess: (_, { chatId }) => {
      // Reset unread count for this chat
      queryClient.setQueriesData<ChatListResponse>(
        { queryKey: queryKeys.messenger.chats() },
        (oldData) => {
          if (!oldData?.items) return oldData;
          return {
            ...oldData,
            items: oldData.items.map((chat) =>
              chat.id === chatId ? { ...chat, unreadCount: 0 } : chat
            ),
          };
        }
      );
      // Invalidate total unread count
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.unreadCount() });
    },
  });
}

// ============================================================================
// Mark All As Read Mutation
// ============================================================================

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    string,
    { previousChats: [readonly unknown[], ChatListResponse | undefined][] }
  >({
    mutationFn: (chatId) => messengerApi.markAllAsRead(chatId),
    onMutate: async (chatId) => {
      // Cancel pending refetches for CHAT LIST queries only. We cannot use
      // queryKeys.messenger.chats() as the key because chat detail
      // (['messenger', 'chats', chatId]) and chat messages
      // (['messenger', 'chats', chatId, 'messages']) share that prefix —
      // a prefix match would silently abort the in-flight useChat(chatId)
      // started by ChatView on mount, which then renders "Чат не найден".
      // Predicate matches only list queries whose 3rd key element is an
      // object (filter) or undefined, never a chatId string.
      await queryClient.cancelQueries({
        predicate: (query) => {
          const k = query.queryKey as readonly unknown[];
          return (
            k.length === 3 &&
            k[0] === 'messenger' &&
            k[1] === 'chats' &&
            (k[2] === undefined || (typeof k[2] === 'object' && k[2] !== null))
          );
        },
      });

      // Snapshot previous state for rollback on error. Uses the same
      // predicate so chat detail queries are not touched.
      const previousChats = queryClient.getQueriesData<ChatListResponse>({
        predicate: (query) => {
          const k = query.queryKey as readonly unknown[];
          return (
            k.length === 3 &&
            k[0] === 'messenger' &&
            k[1] === 'chats' &&
            (k[2] === undefined || (typeof k[2] === 'object' && k[2] !== null))
          );
        },
      });

      // Optimistically zero out unreadCount for this chat in every cached
      // chat list query (there can be several — one per filter/folder combo).
      // This removes the round-trip between click and badge reset, and
      // prevents the badge from flickering if a new message arrives while
      // the markAllAsRead request is in-flight.
      queryClient.setQueriesData<ChatListResponse>(
        { queryKey: queryKeys.messenger.chats() },
        (oldData) => {
          if (!oldData?.items) return oldData;
          return {
            ...oldData,
            items: oldData.items.map((chat) =>
              chat.id === chatId ? { ...chat, unreadCount: 0 } : chat
            ),
          };
        }
      );

      return { previousChats };
    },
    onError: (_error, _chatId, context) => {
      // Rollback on failure so the sidebar badge restores to its prior state.
      if (context?.previousChats) {
        for (const [key, data] of context.previousChats) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSuccess: () => {
      // Refresh the global sidebar badge (total unread across all chats)
      // after the server confirms. Per-chat unreadCount is already zeroed
      // optimistically in onMutate.
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.unreadCount() });
    },
  });
}

// ============================================================================
// Search Messages Hook
// ============================================================================

export function useSearchMessages(query: string, chatId?: string) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: queryKeys.messenger.search(query, chatId),
    queryFn: () => messengerApi.searchMessages(query, chatId),
    enabled: isAuthenticated && query.length >= 2,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Search Messages Infinite Hook (cursor-based pagination for sidebar)
// ============================================================================

export function useSearchMessagesInfinite(
  query: string,
  chatId?: string,
  pageSize: number = 10,
) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useInfiniteQuery<SearchResultResponse, Error>({
    queryKey: queryKeys.messenger.searchInfinite(query, chatId, pageSize),
    queryFn: ({ pageParam }) =>
      messengerApi.searchMessages({
        query,
        chatId,
        pageSize,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    enabled: isAuthenticated && query.length >= 2,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Unread Count Hook (for sidebar badge)
// ============================================================================

export function useMessengerUnreadCount() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<UnreadCountResponse, Error>({
    queryKey: queryKeys.messenger.unreadCount(),
    queryFn: messengerApi.getUnreadCount,
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });
}

// ============================================================================
// Pin / Unpin Message Mutations
// ============================================================================

export function usePinMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, messageId }: { chatId: string; messageId: string }) =>
      messengerApi.pinMessage(chatId, messageId),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chat(chatId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}

export function useUnpinMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: string) => messengerApi.unpinMessage(chatId),
    onSuccess: (_, chatId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chat(chatId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}

/**
 * Ставит эмодзи на сообщение или снимает свою — одно движение для читателя, одна мутация здесь.
 *
 * Кэш правится ОТВЕТОМ: сервер возвращает полный набор реакций сообщения, вместе с посчитанными
 * счётчиками и «моя ли она». Оптимистичной отрисовки нет намеренно — запрос уходит за сотню
 * миллисекунд, а счётчик, мигнувший неверным числом, читается как сбой.
 */
export function useToggleMessageReaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chatId,
      messageId,
      emoji,
      isMine,
    }: {
      chatId: string;
      messageId: string;
      emoji: string;
      isMine: boolean;
    }) =>
      isMine
        ? messengerApi.removeMessageReaction(chatId, messageId, emoji)
        : messengerApi.addMessageReaction(chatId, messageId, emoji),
    onSuccess: (reactions, { chatId, messageId }) => {
      updateMessageInCache(queryClient, chatId, messageId, { reactions });
    },
  });
}

// ============================================================================
// File Messages Hook (Attachments Gallery)
// ============================================================================

export function useFileMessages(chatId: string | null) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useInfiniteQuery<MessageListResponse, Error>({
    queryKey: [...queryKeys.messenger.messages(chatId ?? ''), 'files'],
    queryFn: ({ pageParam }) =>
      messengerApi.getFileMessages(chatId!, {
        cursor: pageParam as string | undefined,
        pageSize: 30,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled: isAuthenticated && !!chatId,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Forward Messages Mutation
// ============================================================================

export function useForwardMessages() {
  const queryClient = useQueryClient();

  return useMutation<
    ChatMessage[],
    Error,
    { targetChatId: string; data: ForwardMessagesRequest }
  >({
    mutationFn: ({ targetChatId, data }) =>
      messengerApi.forwardMessages(targetChatId, data),
    onSuccess: (_, { targetChatId }) => {
      // Fallback invalidation — SignalR usually delivers messages first via ReceiveMessage
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.messages(targetChatId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}

// ============================================================================
// Post Application Card Mutation
// ============================================================================

/**
 * Post an application card into a chat (decision Р27).
 *
 * No optimistic insert, unlike useSendMessage: the server composes both `content`
 * and `payload` from the application id, so anything drawn ahead of the response
 * would be a guess — and a wrong status label in a card is worse than a short wait.
 * Invalidation mirrors useForwardMessages; SignalR normally delivers the message
 * first and this is the fallback.
 */
export function usePostApplicationCard(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation<ChatMessage, Error, string>({
    mutationFn: (applicationId) => messengerApi.postApplicationCard(chatId, applicationId),
    onSuccess: () => {
      // messages() is nested under chats(), so the second call already covers the
      // first; both are spelled out so the intent survives any key restructuring.
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.messages(chatId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
    },
  });
}
